import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { useSession } from '@/app/providers/SessionProvider';
import {
    hasPendingRequest,
    loginPathFor,
    readRequestId,
} from '@/lib/pendingRequest';
import { loginPathWithReturnTo } from '@/lib/returnTo';

/**
 * Pathless route guard (routing.md rule 8) — redirects a signed-out visitor
 * to `/login`. A pending request id is preserved as `request_id`; otherwise the
 * page the visitor wanted is preserved as `next`, so sign-in can bring them
 * back (ticket 825). `replace` so Back does not bounce between the guard and
 * the login screen.
 *
 * A request id that is present but malformed never becomes a `next`: the 739
 * no-provision invariant keys off the *presence* of `request_id`, so such a
 * visit must keep going to a `request_id`-style login path.
 *
 * A deliberate sign-out (`user-signed-out`) goes to a bare `/login`, because
 * the visitor chose to leave rather than lost their session.
 *
 * Generic and reusable for a future dashboard — it knows nothing about
 * consent.
 */
export function RequireSession() {
    const { status, signOutReason } = useSession();
    const [searchParams] = useSearchParams();
    const { pathname, search } = useLocation();

    if (status === 'signed-out') {
        let to: string;
        if (hasPendingRequest(searchParams)) {
            to = loginPathFor(readRequestId(searchParams));
        } else if (signOutReason === 'user-signed-out') {
            // A deliberate sign-out is not a lapse: no `next` to come back to.
            to = '/login';
        } else {
            to = loginPathWithReturnTo(`${pathname}${search}`);
        }

        return <Navigate to={to} replace />;
    }

    return <Outlet />;
}
